/**
 * Shared defensive guards used by `useTranslatedArray`, `useTranslatedRecord`,
 * and their consumers. Centralizes the "translations come from an external
 * file and can ship anything" narrowing so each call site narrows the same
 * way and a future schema change updates one location.
 */

/**
 * Type-guard predicate. Narrows an `unknown` value to a plain object
 * (`Record<string, unknown>`) — i.e. not `null`, not an array, and a `typeof
 * 'object'`. Lets callers drop `as Record<string, unknown>` casts.
 */
export const isPlainObject = (
  value: unknown,
): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Read a string field off a translation record, falling back to `fallback`
 * (default `''`) when the field is missing or not a string. Wraps the
 * `typeof === 'string'` narrowing that translations require because their
 * values are always typed as `unknown` (the i18n file can ship any shape).
 */
export const readString = (
  record: Record<string, unknown>,
  key: string,
  fallback = '',
): string => {
  const value = record[key];
  return typeof value === 'string' ? value : fallback;
};
